/**
 * Visual-only size unit conversion (cm / inch).
 * Original variant values are preserved in data-size-value attributes.
 */
const YarisSizeUnits = {
  STORAGE_KEY: 'yaris-size-unit',
  UNITS: ['cm', 'inch'],

  A_SERIES_MM: [
    ['A6', 105, 148],
    ['A5', 148, 210],
    ['A4', 210, 297],
    ['A3', 297, 420],
    ['A2', 420, 594],
    ['A1', 594, 841],
    ['A0', 841, 1189],
  ],

  SIZE_OPTION_PATTERN: /boyut|size|beden|ölçü|olcu|dimension|ebat/i,

  getUnit() {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored === 'a-series') return 'cm';
    return this.UNITS.includes(stored) ? stored : 'cm';
  },

  setUnit(unit) {
    if (!this.UNITS.includes(unit)) return;
    localStorage.setItem(this.STORAGE_KEY, unit);
    this.syncToggleButtons();
    this.applyAll();
  },

  isDimensionOptionName(name) {
    return this.SIZE_OPTION_PATTERN.test(String(name || ''));
  },

  parseSizeValue(str) {
    if (!str || typeof str !== 'string') return null;
    const trimmed = str.trim();

    const aMatch = trimmed.match(/^A\s*([0-6])$/i);
    if (aMatch) {
      const label = `A${aMatch[1]}`;
      const entry = this.A_SERIES_MM.find(([name]) => name.toLowerCase() === label.toLowerCase());
      if (entry) {
        return { type: 'a', label: entry[0], w: entry[1], h: entry[2], original: trimmed };
      }
    }

    const dimMatch = trimmed.match(
      /(\d+(?:[.,]\d+)?)\s*[x×X]\s*(\d+(?:[.,]\d+)?)(?:\s*(cm|in|inch|inç|"))?(.*)$/i
    );
    if (!dimMatch) return null;

    let w = parseFloat(dimMatch[1].replace(',', '.'));
    let h = parseFloat(dimMatch[2].replace(',', '.'));
    const unitHint = (dimMatch[3] || 'cm').toLowerCase();
    const suffix = (dimMatch[4] || '').trim();

    if (unitHint === 'in' || unitHint === 'inch' || unitHint === 'inç' || unitHint === '"') {
      w *= 25.4;
      h *= 25.4;
    } else {
      w *= 10;
      h *= 10;
    }

    const wMm = Math.min(w, h);
    const hMm = Math.max(w, h);

    return { type: 'dim', w: wMm, h: hMm, original: trimmed, suffix };
  },

  formatNumber(value, decimals = 1) {
    const rounded = Math.round(value * 10 ** decimals) / 10 ** decimals;
    if (Number.isInteger(rounded)) return String(rounded);
    return rounded.toFixed(decimals).replace(/\.0+$/, '');
  },

  convert(parsed, unit) {
    if (!parsed) return null;
    const { w, h, suffix } = parsed;
    let display = null;

    if (unit === 'cm') {
      display = `${this.formatNumber(w / 10, 1)} × ${this.formatNumber(h / 10, 1)} cm`;
    } else if (unit === 'inch') {
      display = `${this.formatNumber(w / 25.4, 1)} × ${this.formatNumber(h / 25.4, 1)} in`;
    }

    if (display && suffix) {
      display = `${display}${suffix.startsWith(' ') ? '' : ' '}${suffix}`;
    }

    return display;
  },

  displayText(original, unit) {
    const parsed = this.parseSizeValue(original);
    if (!parsed) return original;
    return this.convert(parsed, unit) || original;
  },

  applyToElement(el) {
    const original = el.getAttribute('data-size-value');
    if (!original) return;

    const display = el.querySelector('[data-size-display]');
    const text = this.displayText(original, this.getUnit());

    if (display) {
      display.textContent = text;
      return;
    }

    const hidden = el.querySelector('.visually-hidden, .label-unavailable');
    if (hidden) {
      for (const node of el.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
          node.textContent = '';
        }
      }
      const existing = el.querySelector(':scope > [data-size-display]');
      if (existing) {
        existing.textContent = text;
      } else {
        const span = document.createElement('span');
        span.setAttribute('data-size-display', '');
        span.textContent = text;
        el.insertBefore(span, hidden);
      }
      return;
    }

    el.textContent = text;
  },

  applyAll() {
    document.querySelectorAll('[data-size-value]').forEach((el) => this.applyToElement(el));

    document.querySelectorAll('select option[data-size-value]').forEach((option) => {
      if (option.hasAttribute('disabled') || option.hasAttribute('data-crossout')) return;
      const original = option.getAttribute('data-size-value');
      option.textContent = this.displayText(original, this.getUnit());
    });
  },

  syncToggleButtons() {
    const unit = this.getUnit();
    document.querySelectorAll('.size-unit-toggle__btn').forEach((btn) => {
      btn.classList.toggle('is-active', btn.getAttribute('data-size-unit') === unit);
      btn.setAttribute('aria-pressed', btn.getAttribute('data-size-unit') === unit ? 'true' : 'false');
    });
  },

  bindToggles() {
    document.addEventListener('click', (event) => {
      const btn = event.target.closest('.size-unit-toggle__btn');
      if (!btn) return;
      event.preventDefault();
      this.setUnit(btn.getAttribute('data-size-unit'));
    });
  },

  init() {
    this.bindToggles();
    this.syncToggleButtons();
    this.applyAll();

    if (typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        window.requestAnimationFrame(() => this.applyAll());
      });
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => {
        window.requestAnimationFrame(() => {
          this.syncToggleButtons();
          this.applyAll();
        });
      });
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        window.requestAnimationFrame(() => {
          this.syncToggleButtons();
          this.applyAll();
        });
      });
    }

    document.addEventListener('shopify:section:load', () => {
      window.requestAnimationFrame(() => {
        this.syncToggleButtons();
        this.applyAll();
      });
    });
  },
};

window.YarisSizeUnits = YarisSizeUnits;

document.addEventListener('DOMContentLoaded', () => {
  YarisSizeUnits.init();
});
